import { useEffect, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { CheckCheck, FileStack, ScanText, Send } from 'lucide-react';
import { Dialog } from '@paige/ui';
import { explainerSteps, type ExplainerAudience, type ExplainerStep } from './explainer-steps';
import './explainer.css';

const ART: Record<ExplainerStep['art'], ReactNode> = {
  arrive: <FileStack size={22} />,
  read: <ScanText size={22} />,
  check: <CheckCheck size={22} />,
  send: <Send size={22} />,
};

/** Each step stays this long before the next, unless the person is reading (pointer or keyboard inside). */
export const STEP_MS = 3_400;

const stillMotion = (): boolean =>
  typeof window !== 'undefined' &&
  Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);

/** The step on screen, moving on by itself while nobody holds it, round and round; never with reduced motion. */
function useStep(count: number, held: boolean): [number, (step: number) => void] {
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (held || stillMotion()) return undefined;
    const timer = window.setTimeout(() => setStep((s) => (s + 1) % count), STEP_MS);
    return () => window.clearTimeout(timer);
  }, [step, held, count]);
  return [step, setStep];
}

/** The four stations on their line, the lit one current, and the page riding above them. */
function Stations({
  steps,
  step,
  onStep,
}: {
  steps: readonly ExplainerStep[];
  step: number;
  onStep: (step: number) => void;
}): ReactNode {
  return (
    <div className="explainer__stage" style={{ '--step': step } as CSSProperties}>
      <ol className="explainer__track">
        {steps.map((s, i) => (
          <li key={s.art} className={i === step ? 'is-on' : i < step ? 'is-past' : undefined}>
            <button
              type="button"
              aria-current={i === step ? 'step' : undefined}
              aria-label={`${i + 1}. ${s.title}`}
              onClick={() => onStep(i)}
            >
              {ART[s.art]}
            </button>
          </li>
        ))}
      </ol>
      <span className="explainer__page" aria-hidden="true" />
    </div>
  );
}

/**
 * How Paige works, in half a minute (gap list C10): for someone who may not run the sample tour, which makes a
 * job (a reviewer, a scanner, a read-only view). A page travels four stations, each lit as it arrives with one
 * line under it; the stations are buttons, so anyone can step at their own pace, and reduced motion holds it still.
 */
export function TourExplainer({
  audience,
  onClose,
  returnFocusTo,
}: {
  audience: ExplainerAudience;
  onClose: () => void;
  returnFocusTo: RefObject<HTMLElement | null>;
}): ReactNode {
  const steps = explainerSteps(audience);
  const [held, setHeld] = useState(false);
  const [step, setStep] = useStep(steps.length, held);
  const current = steps[step]!;
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      size="md"
      title="How Paige works"
      returnFocusTo={returnFocusTo}
    >
      <div
        className="explainer"
        onPointerEnter={() => setHeld(true)}
        onPointerLeave={() => setHeld(false)}
        onFocus={() => setHeld(true)}
        onBlur={() => setHeld(false)}
      >
        <Stations steps={steps} step={step} onStep={setStep} />
        {/* Read out when the person steps it, not every time it moves on by itself. */}
        <div className="explainer__words" aria-live={held ? 'polite' : 'off'} key={step}>
          <strong>{current.title}</strong>
          <p>{current.line}</p>
        </div>
      </div>
    </Dialog>
  );
}
